import { Grid, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
    ClinicPicker,
    DailyAverageKpi,
    DateRangeFields,
    Header,
    LabDestinationVolumeChart,
    LabPicker,
    ParticipationKpi,
    ScanVolumeByClinicChart,
    Spinner,
    TotalScanVolumeChart,
    TotalScanVolumeKpi,
} from '../../components';
import {
    DashboardProvider,
    useDashboardContext,
} from './context';
import './styles.scss';

function DashboardContent() {
    const theme = useTheme();
    const matchUpLg = useMediaQuery(theme.breakpoints.up('lg'));
    const {
        analyticsData,
        failed,
        loading,
        maxDate,
        minStartDate,
        onUpdateClinic,
        onUpdateDateRange,
        onUpdateLab,
        selectedClinic,
        selectedEndDate,
        selectedLab,
        selectedStartDate,
    } = useDashboardContext();
    const totalScanVolumeChart = analyticsData ? (
        <TotalScanVolumeChart
            analytics={analyticsData.casesByCreatedDate}
            startDate={selectedStartDate}
            endDate={selectedEndDate}
        />
    ) : null;
    const scanVolumeByClinicChart = analyticsData ? (
        <ScanVolumeByClinicChart analytics={analyticsData.casesByClinic} />
    ) : null;
    const labDestinationVolumeChart = analyticsData ? (
        <LabDestinationVolumeChart
            analytics={analyticsData.casesByLab}
            totalAnalytics={analyticsData.casesByCreatedDate}
        />
    ) : null;

    return (
        <>
            <Header label="Dashboard" />
            <main className="dashboard-route-container">
                <Grid container spacing={2} marginBottom={2}>
                    <Grid item md={3} xs={12}>
                        <ClinicPicker
                            selectedClinic={selectedClinic}
                            onSelectClinic={onUpdateClinic}
                        />
                    </Grid>
                    <Grid item md={3} xs={12}>
                        <LabPicker
                            selectedLab={selectedLab}
                            onSelectLab={onUpdateLab}
                        />
                    </Grid>
                    <Grid item md={6} xs={12}>
                        <DateRangeFields
                            initialStartDate={selectedStartDate}
                            initialEndDate={selectedEndDate}
                            minStartDate={minStartDate}
                            maxDate={maxDate}
                            onChangeDateRange={onUpdateDateRange}
                        />
                    </Grid>
                </Grid>
                {loading && <Spinner type="page" />}
                {!loading && failed && <p className="text-red text-align-center">Failed to load dashboard data</p>}
                {!loading && !failed && analyticsData && (
                    <>
                        <Grid container spacing={2} marginBottom={2}>
                            <Grid item lg={4} md={6} xs={12}>
                                <TotalScanVolumeKpi analytics={analyticsData.casesByCreatedDate} />
                            </Grid>
                            <Grid item lg={4} md={6} xs={12}>
                                <DailyAverageKpi
                                    analytics={analyticsData.casesByCreatedDate}
                                    startDate={selectedStartDate}
                                    endDate={selectedEndDate}
                                />
                            </Grid>
                            <Grid item lg={4} md={6} xs={12}>
                                <ParticipationKpi analytics={analyticsData.participation} />
                            </Grid>
                        </Grid>
                        {/* If the screen is large, split into two columns that stack. */}
                        {/* If the screen is small, stack everything in a single column on top of each other. */}
                        {/* NOTE: This means that each chart needs to be added twice. */}
                        {matchUpLg ? (
                            <div className="dashboard-analytics-layout">
                                <div className="dashboard-analytics-layout__column">
                                    {totalScanVolumeChart}
                                    {labDestinationVolumeChart}
                                </div>
                                <div className="dashboard-analytics-layout__column">
                                    {scanVolumeByClinicChart}
                                </div>
                            </div>
                        ) : (
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    {totalScanVolumeChart}
                                </Grid>
                                <Grid item xs={12}>
                                    {scanVolumeByClinicChart}
                                </Grid>
                                <Grid item xs={12}>
                                    {labDestinationVolumeChart}
                                </Grid>
                            </Grid>
                        )}
                    </>
                )}
            </main>
        </>
    );
}

function Dashboard() {
    return (
        <DashboardProvider>
            <DashboardContent />
        </DashboardProvider>
    );
}

export default Dashboard;
